import React, { useState, useRef, useEffect } from 'react';
import { Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import './ContactForm.css';
import { 
    recordSubmissionAttempt, 
    clearSubmissionHistory,
    detectSuspiciousActivity,
    setCooldown,
    getSubmissionStats
} from '../../utils/submissionTracker';
import { trackFormSubmission } from '../../utils/trackingHelper';
import { submitContactFormToAppsScript, submitContactAutoReply } from '../../utils/appsScriptHelper';
import ThankYouPopup from '../ThankYouPopup/ThankYouPopup';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.hansa-flex.co.in';

const ContactForm = () => {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const [formData, setFormData] = useState({
        department: '',
        zone: '',
        name: '',
        phone: '',
        email: '',
        company: '',
        message: '',
        file: null,
        privacyAccepted: false
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showRateLimitWarning, setShowRateLimitWarning] = useState(false);

    // Check if form was just submitted (from query parameter)
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('submitted') === 'true') {
            setShowSuccess(true);
            // Clear submission history after successful submission
            clearSubmissionHistory('contact');
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }
        
        // Check if in cooldown on component mount
        const stats = getSubmissionStats('contact');
        if (stats.inCooldown) {
            setShowRateLimitWarning(true);
        }
    }, []);

    const departments = [
        { value: 'administration', label: t("Administration") },
        { value: 'sales', label: t("Sales") },
        { value: 'technical-department',label: t("Technical Department") },
        { value: 'marketing', label: t("Marketing") },
        { value: 'human-resources', label: t("Human Resources") },
        { value: 'customer-service', label: t("Customer Service") },
        { value: 'other', label: t("Other") },
    ];

    const zones = [
        { value: 'alcala-de-henares-madrid', label: t("Alcalá de Henares – Madrid") },
        { value: 'elgoibar-gipuzkoa', label: t("Elgoibar – Gipuzkoa") },
        { value: 'fuenlabrada-madrid', label: t("Fuenlabrada – Madrid") },
        { value: 'manresa-barcelona', label: t("Manresa – Barcelona") },
    ];

    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB in bytes

    const handleInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        
        // Handle file input with size validation
        if (type === 'file' && files && files[0]) {
            const file = files[0];
            
            // Validate file size
            if (file.size > MAX_FILE_SIZE) {
                setErrors(prev => ({ 
                    ...prev, 
                    file: t('File size exceeds 5 MB limit. Please choose a smaller file.') 
                }));
                // Clear the file input
                e.target.value = '';
                setFormData(prev => ({ ...prev, [name]: null }));
                return;
            }
            
            // Validate file type
            const allowedTypes = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            if (!allowedTypes.includes(fileExtension)) {
                setErrors(prev => ({ 
                    ...prev, 
                    file: t('Invalid file type. Allowed types: PDF, DOC, DOCX, JPG, JPEG, PNG.') 
                }));
                e.target.value = '';
                setFormData(prev => ({ ...prev, [name]: null }));
                return;
            }
            
            setFormData(prev => ({ ...prev, [name]: file }));
            // Clear file error if file is valid
            if (errors.file) {
                setErrors(prev => ({ ...prev, file: '' }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }

        // Clear error when user starts typing
        if (errors[name] && type !== 'file') {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleFileButtonClick = () => {
        fileInputRef.current.click();
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.department) newErrors.department = 'Please select a department';
        if (!formData.zone) newErrors.zone = 'Please select a zone';
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        if (!formData.company.trim()) newErrors.company = 'Company is required';
        if (!formData.privacyAccepted) newErrors.privacyAccepted = 'You must accept the privacy policy';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Check for suspicious activity before submission
        if (detectSuspiciousActivity('contact')) {
            setCooldown('contact');
            setShowRateLimitWarning(true);
            alert(t('Too many submission attempts. Please wait 15 minutes before trying again.'));
            return;
        }

        // Check submission stats
        const stats = getSubmissionStats('contact');
        if (stats.inCooldown) {
            alert(t(`Please wait ${stats.cooldownMinutes} minutes before submitting again.`));
            return;
        }

        // Record this submission attempt
        recordSubmissionAttempt('contact');

        setIsSubmitting(true);

        try {
            // Create FormData object with all form fields
            const formDataToSubmit = new FormData();
            
            // Add form fields
            // API expects stable machine keys (not human labels)
            formDataToSubmit.append('department', formData.department);
            formDataToSubmit.append('zone', formData.zone);
            formDataToSubmit.append('name', formData.name);
            formDataToSubmit.append('phone', formData.phone);
            formDataToSubmit.append('email', formData.email);
            formDataToSubmit.append('company', formData.company);
            formDataToSubmit.append('message', formData.message);
            
            // Add file attachment if present
            if (formData.file) {
                formDataToSubmit.append('attachment', formData.file, formData.file.name);
            }
            
            formDataToSubmit.append('privacyAccepted', formData.privacyAccepted ? 'true' : 'false');

            const response = await fetch(`${API_BASE}/api/spain/contact`, {
                method: 'POST',
                body: formDataToSubmit, // keep FormData (includes attachment)
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || 'Form submission failed');
            }

            // Track successful submission
            trackFormSubmission('contact_form', {
                department: formData.department,
                zone: formData.zone,
                has_attachment: formData.file ? 'yes' : 'no'
            });
            
            // Both Apps Script calls must finish BEFORE the redirect below —
            // navigating away aborts requests still in flight.
            //
            // They go to two different scripts on purpose:
            //   - submitContactFormToAppsScript writes the official Contact
            //     Form sheet, in a Google account we do not control.
            //   - submitContactAutoReply tags the contact by department so
            //     Mailchimp sends the acknowledgement email.
            // Neither can fail the submission: the enquiry already reached
            // HANSA-FLEX through the API call above, so telling the visitor it
            // failed would be wrong. Failures are logged for diagnosis.
            const [, autoReply] = await Promise.all([
                submitContactFormToAppsScript(formData).catch(err => {
                    console.warn('Apps Script log failed (non-critical):', err);
                }),
                submitContactAutoReply(formData).catch(err => {
                    console.warn('Auto-reply trigger failed (non-critical):', err);
                    return { success: false, error: String(err) };
                })
            ]);

            if (autoReply && !autoReply.success) {
                console.warn('[contact] no acknowledgement email sent:', autoReply.error);
            }

            // Redirect to show success message
            window.location.href = window.location.pathname + '?submitted=true';
        } catch (error) {
            console.error('Error submitting form:', error);
            const errorMsg = error.message || t('There was an error submitting the form. Please try again.');
            alert(errorMsg + '\n\nPlease check the browser console for more details.');
            setIsSubmitting(false);
        }
    };

    return (
        <div className="contact-form-container">
            {/* Thank You Popup */}
            <ThankYouPopup
                show={showSuccess}
                onHide={() => setShowSuccess(false)}
                title={t("Thank You!")}
                message={t("Your message has been sent successfully. We will get back to you soon.")}
                buttonText={t("Close")}
            />

            {showRateLimitWarning && (
                <Alert variant="warning" className="mb-4" dismissible onClose={() => setShowRateLimitWarning(false)}>
                    {t("Multiple submission attempts detected. Please wait before trying again.")}
                </Alert>
            )}
            <Form 
                onSubmit={handleSubmit} 
                className="contact-form"
            >
                <Form.Group className="mb-3">
                    <Form.Select
                        name="department"
                        aria-label={t("Department")}
                        value={formData.department}
                        onChange={handleInputChange}
                        isInvalid={!!errors.department}
                        className="form-select-custom"
                    >
                        <option value="">{t("Choose an option")} *</option>
                        {departments.map(dept => (
                            <option key={dept.value} value={dept.value}>
                                {dept.label}
                            </option>
                        ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                        {errors.department}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Select
                        name="zone"
                        aria-label={t("Zone")}
                        value={formData.zone}
                        onChange={handleInputChange}
                        isInvalid={!!errors.zone}
                        className="form-select-custom"
                    >
                        <option value="">{t("Select Zone *")}</option>
                        {zones.map(zone => (
                            <option key={zone.value} value={zone.value}>
                                {zone.label}
                            </option>
                        ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                        {errors.zone}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Control
                        type="text"
                        name="name"
                        placeholder={t("Name *")}
                        value={formData.name}
                        onChange={handleInputChange}
                        isInvalid={!!errors.name}
                        className="form-control-custom"
                    />
                    <Form.Control.Feedback type={t("invalid")}>
                        {errors.name}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Control
                        type="tel"
                        name="phone"
                        placeholder={t("Phone *")}
                        value={formData.phone}
                        onChange={handleInputChange}
                        isInvalid={!!errors.phone}
                        className="form-control-custom"
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.phone}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Control
                        type="email"
                        name="email"
                        placeholder={t("Email *")}
                        value={formData.email}
                        onChange={handleInputChange}
                        isInvalid={!!errors.email}
                        className="form-control-custom"
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.email}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Control
                        type="text"
                        name="company"
                        placeholder={t("Company *")}
                        value={formData.company}
                        onChange={handleInputChange}
                        isInvalid={!!errors.company}
                        className="form-control-custom"
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.company}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                    <div className="textarea-container">
                        <Form.Control
                            as="textarea"
                            name="message"
                            placeholder={t("Message")}
                            value={formData.message}
                            onChange={handleInputChange}
                            rows={2}
                            className="form-control-custom message-textarea"
                        />
                        <div className="google-chat-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" fill="#34A853" />
                                <path d="M8 12h8M12 8v8" stroke="white" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </div>
                    </div>
                </Form.Group>

                <Form.Group className="mb-3">
                    <div className="file-upload-section">
                        <span className="file-size-limit">{t("maximum 5 MB")}</span>
                        <div className="file-upload-container">
                            <Button
                                type="button"
                                variant="outline-secondary"
                                className="choose-file-btn"
                                onClick={handleFileButtonClick}
                            >
                                {t("Choose File")}
                            </Button>
                            <Form.Control
                                ref={fileInputRef}
                                type="file"
                                name="file"
                                // Visually hidden and driven by the button above,
                                // so it needs its own accessible name.
                                aria-label={t("Attach a file")}
                                onChange={handleInputChange}
                                className="hidden-file-input"
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                isInvalid={!!errors.file}
                            />
                            <span className="file-info">
                                {formData.file ? formData.file.name : t("No file chosen")}
                            </span>
                        </div>
                        {errors.file && (
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                {errors.file}
                            </Form.Control.Feedback>
                        )}
                    </div>
                </Form.Group>

                <Form.Group className="mb-0">
                    <Form.Check
                        type="checkbox"
                        // Form.Check only adds htmlFor to its label when it has
                        // an id, so without this the label is not associated.
                        id="privacyAccepted"
                        name="privacyAccepted"
                        checked={formData.privacyAccepted}
                        onChange={handleInputChange}
                        isInvalid={!!errors.privacyAccepted}
                        label={
                            <span>
                               {t("I accept the ")}
                                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="privacy-link">
                                    {t("privacy policy")}
                                </a>
                                  {t(" and ")}
                                <a href="/legal-notice" target="_blank" rel="noopener noreferrer" className="privacy-link">
                                    {t("Legal notice")}
                                </a>
                                .
                            </span>
                        }
                        className="privacy-checkbox"
                    />
                    <Form.Control.Feedback type={t("invalid")}>
                        {errors.privacyAccepted}
                    </Form.Control.Feedback>
                </Form.Group>

                <Row>
                    <Col sm={12} md={10} lg={9}>
                        <div className="data-protection-info">
                            <p>
                                {t("The information you submit through this form is personal, and therefore, we need to remind you of your data protection rights.Data Controller: We inform you that the personal data you provide will be processed by HANSA-FLEX IBÉRICA , S.L. for the purpose of managing your request and providing you with the requested information. The legal basis for the processing is your consent.Your data will not be transferred to third parties unless there is a legal obligation to do so. You have the right to access,rectify, delete, limit, oppose and port your data, as well as to withdraw your consent at any time by contacting us at privacy@hansa-flex.es. You also have the right to file a complaint with the Spanish Data Protection Agency (AEPD).")}
                            </p>
                        </div>
                    </Col>
                    <Col sm={12} md={2} lg={3}>
                        <div className="submit-button-container">
                            <Button 
                                type="submit" 
                                className="submit-button"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? t("Sending...") : t("SEND")}
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default ContactForm; 